/**
 * NdfRenderer
 *
 * Interactive NDF document renderer for the public signer portal.
 * Content blocks render as read-only formatted content.
 * Field blocks render as fillable form elements with validation.
 *
 * Features:
 * - All 16 NDF block types
 * - Conditional logic (show/hide based on field values)
 * - Validation (required, minLength, maxLength, min/max, pattern, email, phone)
 * - Mobile-optimized (44px min touch targets)
 * - Accessibility (ARIA labels, keyboard navigation)
 * - Controlled component (values/errors from props, onChange callback)
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronDown,
  Upload,
  X,
  Check,
  AlertCircle,
  Image as ImageIcon,
} from 'lucide-react';
import { Input, Spinner } from '@respectly/ui';
import type {
  NexusDocument,
  NdfBlock,
  NdfFieldBlock,
  NdfFieldWidth,
  NdfConditionalLogic,
  TextStyle,
} from '@respectly/shared-types';
import {
  isContentBlock,
  isFieldBlock,
  getNdfStyles,
  groupNdfFieldRows,
} from '@respectly/shared-types';
import { SignatureCapture } from './SignatureCapture';

// Tailwind flex sizing for a field width preset. Half/third sit side-by-side on
// sm+ screens and stack to full width on mobile (matches the renderer CSS).
function fieldCellClass(width?: NdfFieldWidth): string {
  if (width === 'half') return 'basis-full sm:basis-[calc(50%-0.5rem)] grow-0 min-w-0';
  if (width === 'third') return 'basis-full sm:basis-[calc(33.333%-0.667rem)] grow-0 min-w-0';
  return 'basis-full min-w-0';
}

// ─── Props ──────────────────────────────────────────────

export interface NdfBrandingProps {
  primaryColor?: string;
  inputBorderRadius?: number;
  inputBorderColor?: string | null;
  inputBackgroundColor?: string | null;
  inputTextColor?: string | null;
}

export interface NdfRendererProps {
  document: NexusDocument;
  values: Record<string, unknown>;
  onChange: (fieldId: string, value: unknown) => void;
  errors?: Record<string, string>;
  readOnly?: boolean;
  onFileUpload?: (file: File) => Promise<string>;
  branding?: NdfBrandingProps;
  /** Template variable values for substitution in content blocks (e.g. {{first_name}}) */
  variables?: Record<string, string>;
  /**
   * Multi-signer NDF: a PRIOR signer's baked signature for a field_signature block id.
   * Such fields render read-only with the signature image — the current signer can't change them.
   */
  lockedSignatures?: Record<string, { imageDataUrl: string; caption?: string }>;
  /**
   * Multi-signer NDF: a PRIOR signer's entered value for a non-signature field id.
   * Such fields render read-only with that value.
   */
  lockedFieldValues?: Record<string, unknown>;
  /**
   * Signature field ids the CURRENT signer may sign. When provided, any field_signature
   * block NOT in this set (and not already locked) renders as "Awaiting another signer".
   */
  signableSignatureFieldIds?: Set<string>;
}

// ─── Conditional Logic ──────────────────────────────────

function evaluateConditionalLogic(
  logic: NdfConditionalLogic,
  values: Record<string, unknown>
): boolean {
  const fieldValue = values[logic.fieldId];
  const compareValue = logic.value;

  switch (logic.operator) {
    case 'equals':
      return fieldValue === compareValue;
    case 'notEquals':
      return fieldValue !== compareValue;
    case 'contains':
      return String(fieldValue ?? '').includes(String(compareValue ?? ''));
    case 'notContains':
      return !String(fieldValue ?? '').includes(String(compareValue ?? ''));
    default:
      return true;
  }
}

function shouldShowBlock(
  block: NdfBlock,
  values: Record<string, unknown>
): boolean {
  if (!('conditionalLogic' in block) || !block.conditionalLogic) return true;
  return evaluateConditionalLogic(block.conditionalLogic, values);
}

function getBlockColSpan(block: NdfBlock, layout: string): string {
  if (layout !== 'two-column') return '';

  const alwaysFullWidth = [
    'paragraph', 'heading', 'list', 'image', 'table',
    'divider', 'spacer', 'section_header', 'page_break',
  ] as const;
  if ((alwaysFullWidth as readonly string[]).includes(block.type)) return 'col-span-2';

  if (block.type === 'field_file' || block.type === 'field_signature') return 'col-span-2';
  if (block.type === 'field_text' && block.inputType === 'textarea') return 'col-span-2';
  if (
    block.type === 'field_select' &&
    (block.selectType === 'radio' || block.selectType === 'checkbox')
  ) return 'col-span-2';

  return '';
}

// ─── Validation ─────────────────────────────────────────

function validateNdfField(
  block: NdfFieldBlock,
  value: unknown
): string | null {
  const validation = block.validation;
  const strValue = String(value ?? '').trim();

  // Required check
  if (validation?.required && !strValue) {
    return validation.customMessage || `${block.label} is required`;
  }

  if (!strValue) return null;

  // Min/max length
  if (validation?.minLength && strValue.length < validation.minLength) {
    return `Minimum ${validation.minLength} characters required`;
  }
  if (validation?.maxLength && strValue.length > validation.maxLength) {
    return `Maximum ${validation.maxLength} characters allowed`;
  }

  // Min/max for numbers
  if (block.type === 'field_text' && block.inputType === 'number' && strValue) {
    const numValue = Number(strValue);
    if (validation?.min !== undefined && numValue < validation.min) {
      return `Minimum value is ${validation.min}`;
    }
    if (validation?.max !== undefined && numValue > validation.max) {
      return `Maximum value is ${validation.max}`;
    }
  }

  // Pattern
  if (validation?.pattern) {
    try {
      const regex = new RegExp(validation.pattern);
      if (!regex.test(strValue)) {
        return validation.customMessage || 'Invalid format';
      }
    } catch {
      // Invalid regex — skip
    }
  }

  // Email
  if (block.type === 'field_text' && block.inputType === 'email' && strValue) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(strValue)) {
      return 'Please enter a valid email address';
    }
  }

  // Phone
  if (block.type === 'field_text' && block.inputType === 'phone' && strValue) {
    const phoneRegex = /^[\d\s\-+()]{10,}$/;
    if (!phoneRegex.test(strValue)) {
      return 'Please enter a valid phone number with country code (e.g. +1 555 123 4567)';
    }
  }

  // Signature required
  if (block.type === 'field_signature' && block.required && !strValue) {
    return `${block.label} is required`;
  }

  return null;
}

// ─── Required state ─────────────────────────────────────
//
// A field's required flag lives on `validation.required` for every block type
// EXCEPT field_signature, which carries its own top-level `required` boolean
// (see ndf.types.ts) — reading only `validation.required` is why a required
// signature field used to render with no asterisk at all.

function isNdfFieldRequired(block: NdfFieldBlock): boolean {
  return block.type === 'field_signature'
    ? block.required
    : !!block.validation?.required;
}

/**
 * True for the field types that render no single control carrying `block.id`:
 * radio/checkbox render one input per option, and file/signature render a
 * trigger <button>. Those are named by pointing a wrapping group at
 * `${block.id}-label` — a `htmlFor` at them would dangle.
 */
function rendersFieldGroup(block: NdfFieldBlock): boolean {
  if (block.type === 'field_select') return block.selectType !== 'dropdown';
  return block.type === 'field_file' || block.type === 'field_signature';
}

/**
 * `aria-required` is only a valid state on a handful of roles — textbox,
 * combobox, checkbox, radiogroup and friends. `role="group"` is NOT one of
 * them, so the checkbox/file/signature groups convey their required state
 * through their accessible name instead (the visually hidden suffix that
 * FieldWrapper adds to the label they point at).
 */
function groupTakesAriaRequired(block: NdfFieldBlock): boolean {
  return block.type === 'field_select' && block.selectType === 'radio';
}

/**
 * The id of the one description FieldWrapper actually has mounted right now.
 * It renders the error INSTEAD of the help text, so an aria-describedby built
 * from both would dangle half the time — and a dangling reference is an axe
 * `aria-valid-attr-value` violation that screen readers announce as nothing.
 */
function fieldDescribedBy(
  block: NdfFieldBlock,
  hasError: boolean,
  fallbackId?: string
): string | undefined {
  if (hasError) return `${block.id}-error`;
  if (block.helpText) return `${block.id}-help`;
  return fallbackId;
}

// ─── TextStyle helpers ──────────────────────────────────

function textStyleToClasses(style?: TextStyle): string {
  if (!style) return '';
  const c: string[] = [];
  if (style.bold) c.push('font-semibold');
  if (style.italic) c.push('italic');
  if (style.align === 'center') c.push('text-center');
  if (style.align === 'right') c.push('text-right');
  return c.join(' ');
}

function textStyleToInline(style?: TextStyle): React.CSSProperties | undefined {
  if (!style?.fontSize) return undefined;
  return { fontSize: `${style.fontSize}px` };
}

// ─── Rich-text rendering for server-resolved merge values ───────────────
//
// The sign-ndf API pre-resolves {{contact.*}}/{{org.*}}/{{custom.*}} tokens
// server-side and wraps each value in a literal HTML string:
//   <strong class="ndf-var-resolved">VALUE</strong>
// (VALUE is HTML-escaped: & < > " ' -> &amp; &lt; &gt; &quot; &#039;).
// Date-family tokens ({{date}}, {{today}}, ...) are left raw for client-side
// resolution in the signer's timezone.
//
// We render the <strong> wrapper as a REAL React element (bold), while keeping
// the surrounding tenant/AI-authored prose as plain React text nodes — those
// are auto-escaped, so any embedded HTML is inert (no sanitizer, no XSS surface).

// Matches the exact wrapper the sign-ndf API emits around resolved values.
// [\s\S] captures multi-line values; non-greedy so adjacent markers stay separate.
const NDF_RESOLVED_MARKER = /<strong class="ndf-var-resolved">([\s\S]*?)<\/strong>/g;

// Reverse the backend's escapeHtml (5 entities). React re-escapes the decoded
// string when rendering it as a text child, so this is safe.
function decodeNdfEntities(s: string): string {
  return s
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&amp;/g, '&'); // must be last
}

/**
 * Render a server-resolved NDF content string as React nodes:
 *  - <strong class="ndf-var-resolved">…</strong> markers -> real bold elements
 *  - client-side {{word}} tokens -> bold if resolvable via `variables`, else left raw
 *  - everything else -> plain text nodes (inert; no HTML is ever parsed)
 */
function renderRichText(
  text: string,
  variables?: Record<string, string>
): React.ReactNode {
  if (!text) return text;

  // 1) Fold client-side {{word}} substitution into the SAME marker grammar so
  //    date tokens etc. also render bold and flow through one tokenizer.
  let prepared = text;
  if (variables) {
    prepared = prepared.replace(/\{\{(\w+)\}\}/g, (match, key: string) => {
      const v = variables[key];
      if (v === undefined) return match; // unknown token -> leave literal
      if (v === '') return ''; // resolved-but-empty -> render nothing
      const escaped = v
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      return `<strong class="ndf-var-resolved">${escaped}</strong>`;
    });
  }

  // 2) Split on markers: even indices = prose, odd indices = captured value.
  const parts = prepared.split(NDF_RESOLVED_MARKER);
  if (parts.length === 1) return prepared; // fast path: no markers

  const nodes: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i++) {
    const chunk = parts[i];
    if (i % 2 === 1) {
      if (!chunk) continue; // skip empty/undefined captures (narrows to string)
      nodes.push(
        <strong key={i} className="ndf-var-resolved">
          {decodeNdfEntities(chunk)}
        </strong>
      );
    } else if (chunk) {
      nodes.push(chunk);
    }
  }
  return nodes;
}

// ─── Field Wrapper Component ─────────────────────────
// Moved outside NdfRenderer to prevent recreation on every render

interface FieldWrapperProps {
  block: NdfFieldBlock;
  children: React.ReactNode;
  touched: Set<string>;
  errors: Record<string, string>;
  variables?: Record<string, string>;
}

function FieldWrapper({ block, children, touched, errors, variables }: FieldWrapperProps) {
  const hasError = touched.has(block.id) && !!errors[block.id];
  const isRequired = isNdfFieldRequired(block);
  const isGroup = rendersFieldGroup(block);

  return (
    <div data-error={hasError ? 'true' : 'false'}>
      {/* Label */}
      <label
        id={`${block.id}-label`}
        // radio/checkbox selects render one input per option and file/signature
        // render a trigger button, so there is no single control for htmlFor to
        // target — those are named via the group's aria-labelledby
        htmlFor={isGroup ? undefined : block.id}
        className="mb-2 block text-sm font-medium text-gray-700"
      >
        {renderRichText(block.label, variables)}
        {isRequired && (
          <span className="ml-1 text-red-500">*</span>
        )}
        {/* The asterisk is visual only. Controls that accept aria-required carry
            it instead; a role="group" cannot, so those fields fold "required"
            into the accessible name the group borrows from this label. */}
        {isRequired && isGroup && !groupTakesAriaRequired(block) && (
          <span className="sr-only"> (required)</span>
        )}
      </label>

      {children}

      {/* Help text */}
      {block.helpText && !hasError && (
        <p id={`${block.id}-help`} className="mt-1 text-xs text-gray-500">
          {renderRichText(block.helpText, variables)}
        </p>
      )}

      {/* Error — id so the control's aria-describedby resolves, role="alert" so
          a failed submit is announced rather than silently scrolled to. */}
      {hasError && (
        <motion.p
          id={`${block.id}-error`}
          role="alert"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-1 flex items-center gap-1 text-sm text-red-600"
        >
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          {errors[block.id]}
        </motion.p>
      )}
    </div>
  );
}

/**
 * Wraps the field types whose control is not a single element carrying
 * `block.id` (radio, checkbox, file, signature). Carries the group's name,
 * required state and description, and `tabIndex={-1}` so a failed submit has
 * something to move focus to.
 */
function FieldGroup({
  block,
  role,
  hasError,
  className,
  children,
}: {
  block: NdfFieldBlock;
  role: 'radiogroup' | 'group';
  hasError: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const required = isNdfFieldRequired(block);
  return (
    <div
      id={`${block.id}-group`}
      role={role}
      tabIndex={-1}
      aria-labelledby={`${block.id}-label`}
      aria-required={required && groupTakesAriaRequired(block) ? true : undefined}
      aria-describedby={fieldDescribedBy(block, hasError)}
      className={className}
    >
      {children}
    </div>
  );
}

// ═══════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════

export function NdfRenderer({
  document: doc,
  values,
  onChange,
  errors: externalErrors,
  readOnly = false,
  onFileUpload,
  branding,
  variables,
  lockedSignatures,
  lockedFieldValues,
  signableSignatureFieldIds,
}: NdfRendererProps) {
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [internalErrors, setInternalErrors] = useState<Record<string, string>>({});
  const [activeSignatureField, setActiveSignatureField] = useState<string | null>(null);
  const [uploadingFields, setUploadingFields] = useState<Set<string>>(new Set());
  const fileInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  // Extract branding values with defaults
  const primaryColor = branding?.primaryColor || '#3b82f6';
  const inputBorderRadius = branding?.inputBorderRadius ?? 8;
  const inputBorderColor = branding?.inputBorderColor;
  const inputBackgroundColor = branding?.inputBackgroundColor;
  const inputTextColor = branding?.inputTextColor;

  // Build input style object from branding
  const inputBrandingStyle: React.CSSProperties = {
    borderRadius: `${inputBorderRadius}px`,
    ...(inputBorderColor && { borderColor: inputBorderColor }),
    ...(inputBackgroundColor && { backgroundColor: inputBackgroundColor }),
    ...(inputTextColor && { color: inputTextColor }),
  };

  // When external errors arrive (e.g. after a submit attempt), mark those
  // fields as touched so FieldWrapper will display the error messages.
  useEffect(() => {
    if (!externalErrors) return;
    const errorFields = Object.keys(externalErrors);
    if (errorFields.length === 0) return;
    setTouched((prev) => {
      const next = new Set(prev);
      errorFields.forEach((id) => next.add(id));
      return next;
    });
  }, [externalErrors]);

  // Merge external + internal errors
  const errors: Record<string, string> = {
    ...internalErrors,
    ...externalErrors,
  };

  const handleChange = useCallback(
    (fieldId: string, value: unknown) => {
      onChange(fieldId, value);

      // Clear internal error on change
      if (internalErrors[fieldId]) {
        setInternalErrors((prev) => {
          const next = { ...prev };
          delete next[fieldId];
          return next;
        });
      }
    },
    [onChange, internalErrors]
  );

  const handleBlur = useCallback(
    (fieldId: string) => {
      setTouched((prev) => new Set(prev).add(fieldId));

      // Find the block and validate
      const block = doc.blocks.find(
        (b) => b.id === fieldId && isFieldBlock(b)
      ) as NdfFieldBlock | undefined;

      if (block) {
        const error = validateNdfField(block, values[fieldId]);
        if (error) {
          setInternalErrors((prev) => ({ ...prev, [fieldId]: error }));
        }
      }
    },
    [doc.blocks, values]
  );

  const handleFileChange = useCallback(
    async (fieldId: string, file: File | null) => {
      if (!file || !onFileUpload) return;

      setUploadingFields((prev) => new Set(prev).add(fieldId));

      try {
        const url = await onFileUpload(file);
        handleChange(fieldId, { url, name: file.name, size: file.size });
      } catch {
        setInternalErrors((prev) => ({
          ...prev,
          [fieldId]: 'Failed to upload file',
        }));
      } finally {
        setUploadingFields((prev) => {
          const next = new Set(prev);
          next.delete(fieldId);
          return next;
        });
      }
    },
    [onFileUpload, handleChange]
  );

  const handleSignatureSave = useCallback(
    (fieldId: string, signatureData: { type: string; data: string }) => {
      handleChange(fieldId, signatureData);
      setActiveSignatureField(null);
    },
    [handleChange]
  );

  // ─── Content block renderers ────────────────────────

  function renderContentBlock(block: NdfBlock) {
    switch (block.type) {
      case 'paragraph': {
        const cls = textStyleToClasses(block.style);
        const inl = textStyleToInline(block.style);
        return (
          <p
            className={`text-sm leading-relaxed text-gray-700 ${cls}`}
            style={inl}
          >
            {renderRichText(block.content, variables)}
          </p>
        );
      }

      case 'heading': {
        const sizes: Record<number, string> = {
          1: 'text-2xl font-bold text-gray-900',
          2: 'text-xl font-bold text-gray-900',
          3: 'text-lg font-semibold text-gray-800',
          4: 'text-base font-semibold text-gray-800',
        };
        // The authored level drives the SIZE, but the tag is clamped to h2-h4:
        // the page already owns the h1, and a raw `h${level}` let a level-1
        // block emit a second h1 and a level-4 one skip two levels (#2197).
        const level = Math.min(4, Math.max(2, block.level));
        const Tag = `h${level}` as 'h2' | 'h3' | 'h4';
        return <Tag className={sizes[block.level] ?? sizes[2]}>{renderRichText(block.content, variables)}</Tag>;
      }

      case 'list': {
        const Tag = block.ordered ? 'ol' : 'ul';
        return (
          <Tag
            className={`space-y-1 pl-5 text-sm text-gray-700 ${
              block.ordered ? 'list-decimal' : 'list-disc'
            }`}
          >
            {block.items.map((item, i) => (
              <li key={i} className="pl-1">
                {renderRichText(item, variables)}
              </li>
            ))}
          </Tag>
        );
      }

      case 'image':
        return (
          <figure className="overflow-hidden rounded-lg">

            <img
              src={block.src}
              alt={block.alt ?? ''}
              width={block.width}
              height={block.height}
              className="max-w-full object-contain"
              onError={(e) => {
                const el = e.currentTarget;
                el.style.display = 'none';
                const sib = el.nextElementSibling;
                if (sib instanceof HTMLElement) sib.style.display = 'flex';
              }}
            />
            <div className="hidden h-24 flex-col items-center justify-center gap-1 bg-gray-50 text-gray-400">
              <ImageIcon className="h-6 w-6" />
              <span className="text-xs">{block.alt || 'Image'}</span>
            </div>
          </figure>
        );

      case 'table':
        return (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  {block.columns.map((col, i) => (
                    <th
                      key={i}
                      className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                    >
                      {renderRichText(col.header, variables)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {block.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci} className="px-4 py-2 text-gray-700">
                        {renderRichText(cell, variables)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'divider':
        return <hr className="border-gray-200" />;

      case 'spacer':
        return <div style={{ height: block.height ?? 32 }} />;

      case 'section_header':
        return (
          <div className="mt-2 border-l-4 py-1 pl-4" style={{ borderColor: primaryColor }}>
            {/* h2, not h3: the page's title is the h1, so a section header is the
                next level down. An h3 here skipped a level (issue #2197). */}
            <h2 className="text-lg font-bold text-gray-900">{renderRichText(block.content, variables)}</h2>
          </div>
        );

      case 'page_break':
        return <div className="border-t-2 border-dashed border-gray-200" />;

      default:
        return null;
    }
  }

  // ─── Field block renderers ──────────────────────────

  function renderFieldBlock(block: NdfFieldBlock) {
    if (readOnly) {
      return renderReadOnlyField(block);
    }

    // ── Multi-signer locking ────────────────────────────
    // A prior signer's baked signature for this field → show it read-only.
    const lockedSig = lockedSignatures?.[block.id];
    if (block.type === 'field_signature' && lockedSig) {
      return renderLockedSignature(block, lockedSig);
    }
    // A signature field that belongs to another signer (not in the current signer's set) →
    // show as awaiting, not signable.
    if (
      block.type === 'field_signature' &&
      signableSignatureFieldIds &&
      !signableSignatureFieldIds.has(block.id)
    ) {
      return renderAwaitingSignature(block);
    }
    // A prior signer's entered value for a non-signature field → show it read-only.
    if (block.type !== 'field_signature' && lockedFieldValues && block.id in lockedFieldValues) {
      return renderReadOnlyField(block, lockedFieldValues[block.id]);
    }

    const value = values[block.id];
    const hasError = touched.has(block.id) && !!errors[block.id];
    const inputErrorClass = hasError
      ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
      : '';
    // Native `required` is inert on this route — NdfRenderer mounts no <form>
    // and nothing styles :invalid — but it populates the accessibility tree
    // alongside aria-required, which is what screen readers announce.
    const requiredProps: { required?: true; 'aria-required'?: true } =
      isNdfFieldRequired(block) ? { required: true, 'aria-required': true } : {};
    const describedById = fieldDescribedBy(block, hasError);

    switch (block.type) {
      case 'field_text': {
        switch (block.inputType) {
          case 'textarea':
            return (
              <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
                <textarea
                  id={block.id}
                  value={String(value ?? '')}
                  onChange={(e) => handleChange(block.id, e.target.value)}
                  onBlur={() => handleBlur(block.id)}
                  placeholder={block.placeholder}
                  rows={4}
                  className={`w-full border border-gray-300 bg-white px-3 py-2 text-sm ${inputErrorClass}`}
                  style={inputBrandingStyle}
                  aria-invalid={hasError}
                  aria-describedby={describedById}
                  {...requiredProps}
                />
              </FieldWrapper>
            );

          case 'email':
            return (
              <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
                <Input
                  id={block.id}
                  type="email"
                  value={String(value ?? '')}
                  onChange={(e) => handleChange(block.id, e.target.value)}
                  onBlur={() => handleBlur(block.id)}
                  placeholder={block.placeholder || 'email@example.com'}
                  className={inputErrorClass}
                  style={inputBrandingStyle}
                  aria-invalid={hasError}
                  aria-describedby={describedById}
                  {...requiredProps}
                />
              </FieldWrapper>
            );

          case 'phone':
            return (
              <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
                <Input
                  id={block.id}
                  type="tel"
                  value={String(value ?? '')}
                  onChange={(e) => handleChange(block.id, e.target.value)}
                  onBlur={() => handleBlur(block.id)}
                  placeholder={block.placeholder || '+1 (555) 123-4567'}
                  className={inputErrorClass}
                  style={inputBrandingStyle}
                  aria-invalid={hasError}
                  aria-describedby={fieldDescribedBy(block, hasError, `${block.id}-phone-helper`)}
                  {...requiredProps}
                />
                {!block.helpText && !hasError && (
                  <p id={`${block.id}-phone-helper`} className="mt-1 text-xs text-gray-500">
                    Include your country code (e.g. +1 for US, +44 for UK)
                  </p>
                )}
              </FieldWrapper>
            );

          case 'number':
            return (
              <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
                <Input
                  id={block.id}
                  type="number"
                  value={String(value ?? '')}
                  onChange={(e) => handleChange(block.id, e.target.value)}
                  onBlur={() => handleBlur(block.id)}
                  placeholder={block.placeholder || '0'}
                  min={block.validation?.min}
                  max={block.validation?.max}
                  className={inputErrorClass}
                  style={inputBrandingStyle}
                  aria-invalid={hasError}
                  aria-describedby={describedById}
                  {...requiredProps}
                />
              </FieldWrapper>
            );

          default:
            // text
            return (
              <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
                <Input
                  id={block.id}
                  type="text"
                  value={String(value ?? '')}
                  onChange={(e) => handleChange(block.id, e.target.value)}
                  onBlur={() => handleBlur(block.id)}
                  placeholder={block.placeholder}
                  className={inputErrorClass}
                  style={inputBrandingStyle}
                  aria-invalid={hasError}
                  aria-describedby={describedById}
                  {...requiredProps}
                />
              </FieldWrapper>
            );
        }
      }

      case 'field_date':
        return (
          <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
            <Input
              id={block.id}
              type="date"
              value={String(value ?? '')}
              onChange={(e) => handleChange(block.id, e.target.value)}
              onBlur={() => handleBlur(block.id)}
              className={inputErrorClass}
              style={inputBrandingStyle}
              aria-invalid={hasError}
              aria-describedby={describedById}
              {...requiredProps}
            />
          </FieldWrapper>
        );

      case 'field_time':
        return (
          <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
            <Input
              id={block.id}
              type="time"
              value={String(value ?? '')}
              onChange={(e) => handleChange(block.id, e.target.value)}
              onBlur={() => handleBlur(block.id)}
              className={inputErrorClass}
              style={inputBrandingStyle}
              aria-invalid={hasError}
              aria-describedby={describedById}
              {...requiredProps}
            />
          </FieldWrapper>
        );

      case 'field_select': {
        switch (block.selectType) {
          case 'dropdown':
            return (
              <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
                <div className="relative">
                  <select
                    id={block.id}
                    value={String(value ?? '')}
                    onChange={(e) => handleChange(block.id, e.target.value)}
                    onBlur={() => handleBlur(block.id)}
                    className={`w-full appearance-none border border-gray-300 bg-white px-3 py-2 pr-10 text-sm ${inputErrorClass}`}
                    style={inputBrandingStyle}
                    aria-invalid={hasError}
                    aria-describedby={describedById}
                    {...requiredProps}
                  >
                    <option value="">
                      {block.placeholder || 'Select an option...'}
                    </option>
                    {block.options.map((opt, i) => (
                      <option key={i} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                </div>
              </FieldWrapper>
            );

          case 'radio':
            return (
              <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
                <FieldGroup
                  block={block}
                  role="radiogroup"
                  hasError={hasError}
                  className="grid grid-cols-1 gap-2 md:grid-cols-2"
                >
                  {block.options.map((opt, i) => (
                    <label
                      key={i}
                      className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50"
                    >
                      <input
                        type="radio"
                        name={block.id}
                        value={opt.value}
                        checked={value === opt.value}
                        onChange={(e) =>
                          handleChange(block.id, e.target.value)
                        }
                        onBlur={() => handleBlur(block.id)}
                        className="h-5 w-5 border-gray-300"
                        style={{ accentColor: primaryColor }}
                      />
                      <span className="text-sm text-gray-700">
                        {opt.label}
                      </span>
                    </label>
                  ))}
                </FieldGroup>
              </FieldWrapper>
            );

          case 'checkbox':
            return (
              <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
                <FieldGroup
                  block={block}
                  role="group"
                  hasError={hasError}
                  className="grid grid-cols-1 gap-2 md:grid-cols-2"
                >
                  {block.options.map((opt, i) => {
                    const checked =
                      Array.isArray(value) && value.includes(opt.value);
                    return (
                      <label
                        key={i}
                        className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 transition-colors hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          value={opt.value}
                          checked={checked}
                          onChange={(e) => {
                            const arr = Array.isArray(value) ? [...value] : [];
                            if (e.target.checked) {
                              arr.push(opt.value);
                            } else {
                              const idx = arr.indexOf(opt.value);
                              if (idx > -1) arr.splice(idx, 1);
                            }
                            handleChange(block.id, arr);
                          }}
                          onBlur={() => handleBlur(block.id)}
                          className="h-5 w-5 rounded border-gray-300"
                          style={{ accentColor: primaryColor }}
                        />
                        <span className="text-sm text-gray-700">
                          {opt.label}
                        </span>
                      </label>
                    );
                  })}
                </FieldGroup>
              </FieldWrapper>
            );

          default:
            return null;
        }
      }

      case 'field_file': {
        const fileData = value as {
          url?: string;
          name?: string;
          size?: number;
        } | null;
        const isUploading = uploadingFields.has(block.id);
        const acceptStr = block.accept?.join(',') ?? '.pdf,.doc,.docx,.jpg,.jpeg,.png,.gif';

        return (
          <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
            <input
              ref={(el) => {
                if (el) fileInputRefs.current.set(block.id, el);
              }}
              type="file"
              id={`${block.id}-file`}
              onChange={(e) =>
                handleFileChange(block.id, e.target.files?.[0] ?? null)
              }
              className="hidden"
              accept={acceptStr}
            />

            {/* The visible control is a <button>, which accepts no
                aria-required — the group hosts the name, required state and
                description instead. */}
            <FieldGroup block={block} role="group" hasError={hasError}>
              {fileData?.url ? (
                <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-center gap-2">
                    <Check className="h-5 w-5 text-green-600" />
                    <span className="text-sm text-gray-700">
                      {fileData.name}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleChange(block.id, null)}
                    aria-label={fileData.name ? `Remove ${fileData.name}` : 'Remove uploaded file'}
                    className="rounded p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() =>
                    fileInputRefs.current.get(block.id)?.click()
                  }
                  disabled={isUploading}
                  className="flex min-h-[100px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-gray-400 hover:bg-gray-100"
                >
                  {isUploading ? (
                    <>
                      <Spinner size="sm" />
                      <span className="mt-2 text-sm text-gray-500">
                        Uploading...
                      </span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-gray-400" />
                      <span className="mt-2 text-sm font-medium text-gray-600">
                        {block.placeholder || 'Click to upload'}
                      </span>
                      {block.accept && block.accept.length > 0 && (
                        <span className="mt-1 text-xs text-gray-500">
                          {block.accept.join(', ')}
                        </span>
                      )}
                      {block.maxSize && (
                        <span className="mt-0.5 text-xs text-gray-400">
                          Max {Math.round(block.maxSize / (1024 * 1024))}MB
                        </span>
                      )}
                    </>
                  )}
                </button>
              )}
            </FieldGroup>
          </FieldWrapper>
        );
      }

      case 'field_signature': {
        const sigData = value as { data?: string } | null;

        return (
          <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
            {/* The visible control is a <button>, which accepts no
                aria-required — the group hosts the name, required state and
                description instead. */}
            <FieldGroup block={block} role="group" hasError={hasError}>
              {sigData?.data ? (
                <div className="relative rounded-lg border bg-white p-4">
    
                  <img
                    src={sigData.data}
                    alt="Signature"
                    className="mx-auto max-h-24 object-contain"
                  />
                  <button
                    type="button"
                    onClick={() => handleChange(block.id, null)}
                    aria-label="Clear signature"
                    className="absolute right-2 top-2 rounded p-1 text-gray-400 hover:text-red-500"
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setActiveSignatureField(block.id)}
                  className="flex min-h-[100px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white transition-colors"
                  style={{
                    borderRadius: `${inputBorderRadius}px`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = primaryColor;
                    e.currentTarget.style.backgroundColor = `${primaryColor}08`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.backgroundColor = 'white';
                  }}
                >
                  <span className="text-sm font-medium" style={{ color: primaryColor }}>
                    Click to sign
                  </span>
                </button>
              )}
            </FieldGroup>
          </FieldWrapper>
        );
      }

      default:
        return null;
    }
  }

  // ─── Locked signature (a prior signer's baked signature) ───
  function renderLockedSignature(
    block: NdfFieldBlock,
    sig: { imageDataUrl: string; caption?: string }
  ) {
    return (
      <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
        <div className="rounded-lg border bg-white p-4">
          <img
            src={sig.imageDataUrl}
            alt="Signature"
            className="mx-auto max-h-24 object-contain"
          />
          {sig.caption && (
            <p className="mt-2 text-center text-xs text-gray-400">{sig.caption}</p>
          )}
        </div>
      </FieldWrapper>
    );
  }

  // ─── Awaiting signature (another signer's field) ───────────
  function renderAwaitingSignature(block: NdfFieldBlock) {
    return (
      <FieldWrapper block={block} touched={touched} errors={errors} variables={variables}>
        <div className="flex min-h-[80px] w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 text-center">
          <span className="text-sm font-medium text-gray-400">Awaiting another signer</span>
        </div>
      </FieldWrapper>
    );
  }

  // ─── Read-only field view ───────────────────────────

  function renderReadOnlyField(block: NdfFieldBlock, valueOverride?: unknown) {
    const value = valueOverride !== undefined ? valueOverride : values[block.id];
    const displayValue = value !== null && value !== undefined ? String(value) : '';

    return (
      <div className="mb-4">
        <p className="mb-1 text-sm font-medium text-gray-700">
          {renderRichText(block.label, variables)}
        </p>
        {displayValue ? (
          <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-800">
            {block.type === 'field_signature' ? (
              <img
                src={
                  (value as { data?: string })?.data ?? ''
                }
                alt="Signature"
                className="max-h-16 object-contain"
              />
            ) : (
              displayValue
            )}
          </p>
        ) : (
          <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-400">
            Not provided
          </p>
        )}
      </div>
    );
  }

  // ─── Block dispatcher ───────────────────────────────

  function renderBlock(block: NdfBlock) {
    // Skip hidden conditional blocks
    if (!shouldShowBlock(block, values)) return null;

    // PDF overlay blocks are not rendered inline
    if (block.type === 'pdf_overlay') return null;

    if (isContentBlock(block)) {
      return renderContentBlock(block);
    }

    if (isFieldBlock(block)) {
      return renderFieldBlock(block);
    }

    return null;
  }

  // ─── Render ─────────────────────────────────────────

  const layout = doc.meta.layout ?? 'single';

  // Generate unique ID for scoped styles
  const scopeId = `ndf-${doc.meta.title?.replace(/\s+/g, '-').toLowerCase() || 'form'}`;

  return (
    <div data-ndf-scope={scopeId}>
      {/* Shared NDF stylesheet — applies legal document typography via [data-ndf-kind] selector */}
      <div dangerouslySetInnerHTML={{ __html: getNdfStyles() }} />
      {/* Scoped styles for focus states using primaryColor */}
      <style>{`
        [data-ndf-scope="${scopeId}"] input:focus,
        [data-ndf-scope="${scopeId}"] textarea:focus,
        [data-ndf-scope="${scopeId}"] select:focus {
          border-color: ${primaryColor} !important;
          box-shadow: 0 0 0 3px ${primaryColor}20 !important;
          outline: none !important;
        }
        [data-ndf-scope="${scopeId}"] input[type="radio"]:checked,
        [data-ndf-scope="${scopeId}"] input[type="checkbox"]:checked {
          background-color: ${primaryColor} !important;
          border-color: ${primaryColor} !important;
        }
        [data-ndf-scope="${scopeId}"] input[type="radio"]:focus,
        [data-ndf-scope="${scopeId}"] input[type="checkbox"]:focus {
          ring-color: ${primaryColor} !important;
          box-shadow: 0 0 0 2px ${primaryColor}40 !important;
        }
      `}</style>
      <div
        className={`ndf-document ndf-layout-${layout} ${
          layout === 'two-column'
            ? 'grid grid-cols-1 gap-x-6 gap-y-4 lg:grid-cols-2'
            : 'space-y-4'
        }`}
        data-ndf-kind={doc.kind}
      >
        {groupNdfFieldRows(doc.blocks).map((row, idx) => {
          if (row.kind === 'block') {
            const block = row.block;
            const rendered = renderBlock(block);
            if (!rendered) return null;
            return (
              <AnimatePresence key={block.id} mode="wait">
                {shouldShowBlock(block, values) && (
                  <motion.div
                    className={getBlockColSpan(block, layout)}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    {rendered}
                  </motion.div>
                )}
              </AnimatePresence>
            );
          }

          // A run of field blocks — lay out side-by-side per their width presets.
          return (
            <div
              key={`fields-${row.fields[0]?.id ?? idx}`}
              className={`flex flex-wrap items-start gap-4 ${layout === 'two-column' ? 'col-span-2' : ''}`}
            >
              {row.fields.map((field) => {
                const rendered = renderBlock(field);
                if (!rendered) return null;
                return (
                  <AnimatePresence key={field.id} mode="wait">
                    {shouldShowBlock(field, values) && (
                      <motion.div
                        className={fieldCellClass(field.width)}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        {rendered}
                      </motion.div>
                    )}
                  </AnimatePresence>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Signature Capture Modal */}
      <SignatureCapture
        isOpen={!!activeSignatureField}
        onClose={() => setActiveSignatureField(null)}
        onSave={(data) => {
          if (activeSignatureField) {
            handleSignatureSave(activeSignatureField, data);
          }
        }}
        fieldLabel={
          activeSignatureField
            ? (doc.blocks.find((b) => b.id === activeSignatureField) as NdfFieldBlock | undefined)
                ?.label
            : undefined
        }
      />
    </div>
  );
}

export default NdfRenderer;
